import { get } from "lodash";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Field } from "formik";
import { Segment, Label, Button, Form, Divider } from "semantic-ui-react";

import FormikSemanticInput from "../../forms/formik/FormikSemanticInput.jsx";
import FormikSemanticDropdown from "../../forms/formik/FormikSemanticDropdown.jsx";
import FormikSemanticSlider from "../../forms/formik/FormikSemanticSlider.jsx";
import ResourceBlockMultiples from "./ResourceBlockMultiples.jsx";

import {
  selectImagesList,
  selectSizesList,
  selectImageId,
  selectImage,
  selectSize,
  selectVolumes
} from "../../../state/reducers/projectWizard/projectWizardSelectors.js";
import { selectCopy } from "../../../state/reducers/locale/copySelector.js";

class ResourceBlock extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    configCopyKey: PropTypes.string,
    configOptions: PropTypes.object.isRequired,
    enableMultiples: PropTypes.bool.isRequired,
    onRemove: PropTypes.func.isRequired,

    resourceType: PropTypes.string.isRequired, // required for selectors to function
    formikProps: PropTypes.object.isRequired, // required for selectors to function

    imageAtIdt: PropTypes.string,
    imagesList: PropTypes.array.isRequired,
    selectedImage: PropTypes.object,
    sizesList: PropTypes.array.isRequired,
    selectedSize: PropTypes.object,
    apiErrors: PropTypes.object,
    volumes: PropTypes.array,
    maxAvailableInstances: PropTypes.number.isRequired,
    onShowOrHideMultiples: PropTypes.func.isRequired,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    id: "resource[0]",
    enableMultiples: false,
    configOptions: {},
    maxAvailableInstances: 1,
    copyKey: "project_wizard.resource_panel"
  };

  // -- DATA MANAGEMENT -- //

  static newInstance(resourceType) {
    return {
      "@type": resourceType,
      name: "",
      description: "",
      image: null,
      size: null,

      // temp to prevent controlled component warnings
      rootVolumeSliderSize: 0,
      dataVolumeSliderSize: 0
    };
  }

  componentDidUpdate(prevProps) {
    const { formikProps, imageAtId, volumes } = this.props;

    if (prevProps.imageAtId !== imageAtId) {
      // reset and clear all existing volume values
      prevProps.volumes.forEach(volume => {
        const { name, count, size } = this.getDerivedVolumeIds(volume);
        formikProps.setFieldValue(name, 0);
        formikProps.setFieldValue(size, undefined);
        formikProps.setFieldValue(count, undefined);
      });

      // set new initial volume values
      volumes.forEach(volume => {
        const { name, count, size } = this.getDerivedVolumeIds(volume);
        const suggested = get(volume, "size.suggested", 0);
        const chunk = get(volume, "capacity", 1);

        formikProps.setFieldValue(name, suggested);
        formikProps.setFieldValue(size, chunk < 1 ? suggested : chunk);
        formikProps.setFieldValue(count, chunk < 1 ? 1 : suggested / chunk);
      });
    }
  }

  // -- HELPERS -- //

  getDerivedVolumeIds = volume => {
    const { copy, copyKey, id } = this.props;

    let label, name, count, size;

    if (volume.name) {
      label = copy(`${copyKey}.volumes.${volume.name}.label`);
      name = `${id}.${volume.name}VolumeSliderSize`;
      count = `${id}.${volume.name}VolumeCount`;
      size = `${id}.${volume.name}VolumeSize`;
    } else {
      label = copy(`${copyKey}.volumes.root.label`);
      name = `${id}.volumeSliderSize`;
      count = `${id}.volumeCount`;
      size = `${id}.volumeSize`;
    }

    return { label, name, count, size };
  };

  // -- RENDER -- //

  renderVolumeSliders = () => {
    const { apiErrors, volumes } = this.props;

    if (!volumes) return null;

    return volumes.map(volume => {
      const volumeSize = get(volume, "size", null);

      if (volumeSize) {
        const { label, name, count, size } = this.getDerivedVolumeIds(volume);

        return (
          <Field
            key={volume.name}
            name={name}
            label={label}
            count={count}
            size={size}
            chunk={volume.capacity}
            min={volumeSize.minimum}
            max={volumeSize.maximum}
            step={volumeSize.step}
            required={true}
            apiErrors={apiErrors}
            component={FormikSemanticSlider}
          />
        );
      }
    });
  };

  renderBody = () => {
    const {
      id,
      formikProps,
      enableMultiples,
      maxAvailableInstances,
      onShowOrHideMultiples,
      onRemove,
      imagesList,
      selectedImage,
      sizesList,
      selectedSize,
      apiErrors,
      configOptions: { singleMode },
      copyKey,
      copy
    } = this.props;

    const showMultiples = get(
      formikProps,
      `values.${id}.namingScheme`,
      undefined
    )
      ? true
      : false;

    return (
      <Fragment>
        {singleMode !== true && (
          <Label attached="top">
            <Button
              data-testid={`removeBlock${id}`}
              onClick={() => onRemove(id)}
              icon="minus circle"
              size="mini"
              floated="right"
              type="button"
            />
          </Label>
        )}
        <Divider hidden />
        <Form.Group widths="equal" className="clear-both">
          <Field
            name={`${id}.name`}
            label={copy(`${copyKey}.name_label`)}
            placeholder={copy(`${copyKey}.name_placeholder`)}
            required={!showMultiples}
            // autoFocus={!showMultiples}
            disabled={showMultiples}
            apiErrors={apiErrors}
            component={FormikSemanticInput}
          />
          <Field
            name={`${id}.description`}
            label={copy(`${copyKey}.description_placeholder`)}
            placeholder={copy(`${copyKey}.description_label`)}
            apiErrors={apiErrors}
            component={FormikSemanticInput}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <Field
            name={`${id}.image`}
            className="image-icon-dropdown"
            label={copy(`${copyKey}.image_placeholder`)}
            help={selectedImage.description}
            placeholder={copy(`${copyKey}.image_label`)}
            options={imagesList}
            required={true}
            apiErrors={apiErrors}
            onChange={this.handleImageChange}
            component={FormikSemanticDropdown}
          />
          <Field
            name={`${id}.size`}
            label={copy(`${copyKey}.size_placeholder`)}
            placeholder={copy(`${copyKey}.size_label`)}
            help={selectedSize.description}
            options={sizesList}
            required={true}
            apiErrors={apiErrors}
            component={FormikSemanticDropdown}
          />
        </Form.Group>
        {this.renderVolumeSliders()}
        {enableMultiples && (
          <ResourceBlockMultiples
            id={id}
            formikProps={formikProps}
            apiErrors={apiErrors}
            maxAvailableInstances={maxAvailableInstances}
            onShowOrHideMultiples={onShowOrHideMultiples}
            copyKey={copyKey}
            copy={copy}
          />
        )}
      </Fragment>
    );
  };

  render() {
    const {
      id,
      configOptions: { singleMode }
    } = this.props;

    if (singleMode !== true) {
      return (
        <Segment key={id} className="clear-both">
          {this.renderBody()}
        </Segment>
      );
    }

    return this.renderBody();
  }
}

const mapStateToProps = (state, props) => ({
  imageAtId: selectImageId(state, props),
  imagesList: selectImagesList(state, props),
  selectedImage: selectImage(state, props),
  sizesList: selectSizesList(state, props),
  selectedSize: selectSize(state, props),
  volumes: selectVolumes(state, props),
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(ResourceBlock);
